import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider as CoreThemeProvider, createMuiTheme as createCoreTheme } from '@material-ui/core/styles';
import { ThemeProvider as MuiThemeProvider, createTheme as createMuiTheme } from '@mui/material/styles';
import Cookies from 'js-cookie';
import api from '../../../../../services/api';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    useMediaQuery,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Tooltip
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneIcon from '@mui/icons-material/Done';
import ScheduleIcon from '@mui/icons-material/Schedule';

moment.locale('pt-br');

// Configurações de tempo para cálculo de horas passadas
const calcularTempoPassado = (dataString) => {
    if (!dataString) return 'Data não informada';
    const mData = moment(dataString);
    if (!mData.isValid()) return 'Data inválida';
    return mData.fromNow();
};

// Funções de formatação e cores
const formataStatusOS = (status) => {
    const mapa = {
        0: { label: 'Aberta', color: 'info' },
        1: { label: 'Em tratamento remoto', color: 'warning' },
        2: { label: 'Em Analise Previa', color: 'secondary' },
        3: { label: 'Tratamento em Campo', color: 'primary' },
        9: { label: 'Concluída', color: 'success' }
    };
    return mapa[status] || { label: 'Desconhecido', color: 'default' };
};

const formataStatusProced = (status) => {
    const mapa = {
        0: { label: 'Pendente', color: 'error' },
        1: { label: 'Concluído', color: 'success' },
        2: { label: 'Pausado', color: 'warning' },
        9: { label: 'Cancelado', color: 'default' }
    };
    return mapa[status] || { label: 'Desconhecido', color: 'default' };
};


export default function VisaoGeral() {
    const defaultIsMobile = useMediaQuery('(max-width:900px)');
    const isMobile = defaultIsMobile;

    const [darkMode, setDarkMode] = useState(() => {
        const saved = Cookies.get('darkMode') || Cookies.get('tema');
        return saved === 'true' || saved === 'dark';
    });

    const [loading, setLoading] = useState(true);
    const [dados, setDados] = useState({
        ultimas_os: [],
        os_paradas: [],
        procedimentos_parados: [],
        kpi_abertas_mes: 0,
        kpi_finalizadas_mes: 0,
        kpi_paradas_semana: 0,
        ranking_solicitacoes: [],
        recentes_executores: []
    });

    useEffect(() => {
        const checkDarkMode = () => {
            const saved = Cookies.get('darkMode') || Cookies.get('tema');
            setDarkMode(saved === 'true' || saved === 'dark');
        };

        window.addEventListener('temaChanged', checkDarkMode);
        window.addEventListener('darkModeChanged', checkDarkMode);
        window.addEventListener('focus', checkDarkMode);

        const intervalId = setInterval(checkDarkMode, 1000);

        return () => {
            window.removeEventListener('temaChanged', checkDarkMode);
            window.removeEventListener('darkModeChanged', checkDarkMode);
            window.removeEventListener('focus', checkDarkMode);
            clearInterval(intervalId);
        };
    }, []);

    const fetchDados = async () => {
        try {
            setLoading(true);
            const response = await api.get('v1/protected/ordemservico/dashboardvisaogeral');
            if (response.data) {
                // Merge para garantir que campos não presentes na resposta não fiquem undefined
                setDados(prev => ({ ...prev, ...response.data }));
            }
        } catch (error) {
            console.error("Erro ao buscar visão geral:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDados();
    }, []);

    // ThemeHelper sincronizado com index.js e requerimentoSaidaEstoque.js
    const themeHelper = useMemo(() => {
        return {
            bg: darkMode ? "#252525" : "#e9eff3",
            paper: darkMode ? "#2e2e2e" : "#f5f8fa",
            text: darkMode ? "#ffffff" : "#1a1a1a",
            subText: darkMode ? "#aaaaaa" : "#5a6a7a",
            border: darkMode ? "#444444" : "#d1d9e0",
            primary: darkMode ? '#bb86fc' : '#1976d2',
            secondary: darkMode ? '#03dac6' : '#dc004e',
            tableHead: darkMode ? '#333333' : '#f5f5f5',
        };
    }, [darkMode]);

    // Temas V5 e V4 sincronizados
    const muiTheme = useMemo(() => createMuiTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
            primary: { main: themeHelper.primary },
            secondary: { main: themeHelper.secondary },
            background: { default: darkMode ? '#121212' : '#ffffff', paper: darkMode ? '#1e1e1e' : '#ffffff' },
            text: { primary: themeHelper.text, secondary: themeHelper.subText },
        },
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            h5: { fontWeight: 600 },
            h6: { fontWeight: 600 }
        },
        components: {
            MuiTextField: {
                styleOverrides: {
                    root: darkMode ? {
                        '& label.Mui-focused': { color: 'white' },
                        '& label': { color: 'white' },
                        '& .MuiInput-underline:before': { borderBottomColor: 'white' },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'white' },
                        '& .MuiInput-underline:after': { borderBottomColor: 'white' },
                        '& .MuiInputBase-input': { color: 'white' },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'white' },
                            '&:hover fieldset': { borderColor: 'white' },
                            '&.Mui-focused fieldset': { borderColor: 'white' },
                        },
                    } : {},
                }
            }
        }
    }), [darkMode, themeHelper]);

    const coreTheme = useMemo(() => createCoreTheme({
        palette: {
            type: darkMode ? 'dark' : 'light',
            primary: { main: themeHelper.primary },
            secondary: { main: themeHelper.secondary },
            background: { default: darkMode ? '#121212' : '#ffffff', paper: darkMode ? '#1e1e1e' : '#ffffff' },
            text: { primary: darkMode ? '#ffffff' : '#000000', secondary: darkMode ? '#aaaaaa' : '#666666' }
        },
        overrides: {
            MuiTextField: {
                root: darkMode ? {
                    '& label.Mui-focused': { color: 'white' },
                    '& label': { color: 'white' },
                    '& .MuiInput-underline:before': { borderBottomColor: 'white' },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'white' },
                    '& .MuiInput-underline:after': { borderBottomColor: 'white' },
                    '& .MuiInputBase-input': { color: 'white' },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'white' },
                        '&:hover fieldset': { borderColor: 'white' },
                        '&.Mui-focused fieldset': { borderColor: 'white' },
                    },
                } : {},
            },
            MuiFormLabel: {
                root: darkMode ? { color: 'white', '&$focused': { color: 'white' } } : {}
            },
            MuiInputBase: {
                input: darkMode ? { color: 'white' } : {}
            }
        }
    }), [darkMode, themeHelper]);

    // Sub-componentes
    const KPICard = ({ title, value, icon, color }) => (
        <Card sx={{ bgcolor: themeHelper.paper, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="caption" color={themeHelper.subText} sx={{ fontWeight: 600, textTransform: 'uppercase' }}>{title}</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: themeHelper.text, mt: 0.5 }}>{value}</Typography>
                    </Box>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {React.cloneElement(icon, { sx: { color: color, fontSize: 32 } })}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );

    // Helper para renderizar linhas das tabelas de OS (Ultimas e Antigas)
    const renderOSTableRows = (lista = []) => {
        return (lista || []).slice(0, 10).map((os, i) => (
            <TableRow key={i} hover>
                <TableCell
                    sx={{
                        color: themeHelper.text,
                        py: 0.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                    }}
                    onClick={() => window.open(`/gestao/administracao/ordensdeservicos/editar/${os.id_ordem_servico}`, '_blank')}
                >
                    <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ color: themeHelper.primary, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                        #{os.id_ordem_servico}
                    </Typography>
                    <Typography variant="caption" color={themeHelper.subText} display="block">
                        {os.data_hora_criacao ? moment(os.data_hora_criacao).format('DD/MM/YY HH:mm') : '-'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: themeHelper.primary, fontStyle: 'italic' }}>
                        {calcularTempoPassado(os.data_hora_criacao)}
                    </Typography>
                </TableCell>
                <TableCell sx={{ color: themeHelper.text, maxWidth: 180, py: 0.5 }} noWrap>
                    {os.solicitacao || os.descricao_solicitacao || '-'}
                </TableCell>
                <TableCell sx={{ color: themeHelper.text, maxWidth: 150, py: 0.5 }} noWrap>
                    {os.instalacao_nome || '-'}
                </TableCell>
                <TableCell sx={{ color: themeHelper.text, py: 0.5 }}>{os.criador || '-'}</TableCell>
                <TableCell sx={{ py: 0.5 }}>
                    <Chip size="small" label={formataStatusOS(os.status_ordem).label} color={formataStatusOS(os.status_ordem).color} sx={{ height: 20, fontSize: '0.7rem', mb: 0.5 }} />
                    {(os.status_ordem === 2 || os.status_ordem === 3) && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Tooltip title={os.veiculo_placa ? `${os.veiculo_modelo} (${os.veiculo_placa})` : "Veículo não selecionado"} arrow>
                                <Chip size="small" label="Veiculo" variant="outlined" color={os.veiculo_placa ? "success" : "error"} sx={{ height: 16, fontSize: '0.6rem' }} />
                            </Tooltip>
                            <Tooltip title={os.colaboradores_nomes || "Nenhum colaborador designado"} arrow>
                                <Chip size="small" label="Colaboradores" variant="outlined" color={os.colaboradores_nomes ? "success" : "error"} sx={{ height: 16, fontSize: '0.6rem' }} />
                            </Tooltip>
                            <Tooltip title={os.total_itens > 0 ? "Itens Selecionados" : "Itens não selecionados"} arrow>
                                <Chip size="small" label="Itens" variant="outlined" color={os.total_itens > 0 ? "success" : "error"} sx={{ height: 16, fontSize: '0.6rem' }} />
                            </Tooltip>
                        </Box>
                    )}
                </TableCell>
                <TableCell sx={{ color: themeHelper.text, py: 0.5 }}>
                    {(os.status_ordem === 2 || os.status_ordem === 3) && (os.data_agendada || os.equipe_nome || os.veiculo_placa) ? (
                        <Box>
                            {os.data_agendada && (
                                <>
                                    <Typography variant="caption" display="block" fontWeight="bold">
                                        📅 {moment(os.data_agendada).format('DD/MM HH:mm')} - {os.data_agendada_fim ? moment(os.data_agendada_fim).format('DD/MM HH:mm') : '?'}
                                    </Typography>
                                    {(() => {
                                        const inicio = moment(os.data_agendada);
                                        const fim = os.data_agendada_fim ? moment(os.data_agendada_fim) : null;
                                        const agora = moment();

                                        if (fim && agora.isBetween(inicio, fim)) {
                                            return <Chip size="small" label="Em execução" color="success" sx={{ height: 16, fontSize: '0.6rem', mt: 0.5 }} />;
                                        } else if (agora.isBefore(inicio)) {
                                            return <Chip size="small" label="Não iniciado" color="warning" sx={{ height: 16, fontSize: '0.6rem', mt: 0.5 }} />;
                                        }
                                        return null;
                                    })()}
                                </>
                            )}
                            {os.equipe_nome && <Typography variant="caption" display="block">👥 {os.equipe_nome}</Typography>}
                            {os.veiculo_placa && <Typography variant="caption" display="block">🚗 {os.veiculo_modelo} ({os.veiculo_placa})</Typography>}
                        </Box>
                    ) : (
                        <Typography variant="caption" color={themeHelper.subText}>-</Typography>
                    )}
                </TableCell>
                <TableCell sx={{ py: 0.5, minWidth: 120 }}>
                    {os.total_tarefas > 0 ? (
                        <Tooltip
                            arrow
                            title={
                                <Box sx={{ p: 1 }}>
                                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>Procedimentos do Tratamento em Campo:</Typography>
                                    {(os.procedimentos_detalhes || "").split(';;').map((procStr, idx) => {
                                        const parts = procStr.split('|');
                                        if (parts.length < 4) return null;
                                        const [nome, executor, data, status] = parts;
                                        return (
                                            <Box key={idx} sx={{ mb: 0.5, p: 0.5, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                                                <Typography variant="caption" display="block" sx={{ color: '#fff', fontSize: '0.7rem' }}>
                                                    {status === '1' ? '✅' : '⏳'} <strong>{nome}</strong>
                                                </Typography>
                                                <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>
                                                    👤 {executor} | 📅 {data}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            }
                        >
                            <Box sx={{ cursor: 'help' }}>
                                <Typography variant="caption" display="block" color="success.main" fontWeight="bold">
                                    ✅ {((os.tarefas_concluidas / os.total_tarefas) * 100).toFixed(0)}% concluído
                                </Typography>
                                <Typography variant="caption" display="block" color="warning.main" fontWeight="bold">
                                    ⏳ {(((os.total_tarefas - os.tarefas_concluidas) / os.total_tarefas) * 100).toFixed(0)}% por concluir
                                </Typography>
                            </Box>
                        </Tooltip>
                    ) : (
                        <Typography variant="caption" color={themeHelper.subText}>-</Typography>
                    )}
                </TableCell>
            </TableRow>
        ));
    };

    const RenderKPIs = () => (
        <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
                <KPICard title="Abertas no Mês" value={dados.kpi_abertas_mes} icon={<TimelineIcon />} color={themeHelper.primary} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <KPICard title="Finalizadas (Mês)" value={dados.kpi_finalizadas_mes} icon={<CheckCircleIcon />} color="#4caf50" />
            </Grid>
            <Grid item xs={12} sm={4}>
                <KPICard title="Paradas > 1 Semana" value={dados.kpi_paradas_semana} icon={<ReportProblemIcon />} color="#f44336" />
            </Grid>
        </Grid>
    );

    const RenderRankingSolicitacoes = () => (
        <Card sx={{ bgcolor: themeHelper.paper, borderRadius: 2, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold" color={themeHelper.text} mb={1.5}>Solicitações Mais Comuns</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(dados?.ranking_solicitacoes || []).map((item, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderRadius: 1, bgcolor: darkMode ? 'rgba(255,255,255,0.03)' : '#f8f9fa' }}>
                            <Typography variant="body2" sx={{ color: themeHelper.text, flex: 1 }} noWrap>{item.label}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: themeHelper.primary, ml: 2 }}>{item.valor}</Typography>
                        </Box>
                    ))}
                    {(!dados?.ranking_solicitacoes || dados.ranking_solicitacoes.length === 0) && (
                        <Typography variant="caption" color={themeHelper.subText} textAlign="center" py={2}>Nenhum dado disponível.</Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );

    const RenderExecutoresRecentes = () => (
        <Card sx={{ bgcolor: themeHelper.paper, borderRadius: 2, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold" color={themeHelper.text} mb={1.5}>Execução Recente</Typography>
                <TableContainer sx={{ overflowX: 'hidden' }}>
                    <Table size="small">
                        <TableBody>
                            {(dados?.recentes_executores || []).map((item, i) => (
                                <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                                    <TableCell sx={{ color: themeHelper.text, py: 0.8, pl: 0 }} noWrap>
                                        <Typography variant="body2" fontWeight="bold">#{item.id_ordem_servico}</Typography>
                                        <Typography variant="caption" color={themeHelper.subText}>{item.solicitacao || '-'}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ color: themeHelper.text, py: 0.8, pr: 0 }} align="right">
                                        <Chip size="small" label={item.executor} sx={{ height: 20, fontSize: '0.7rem', bgcolor: darkMode ? '#333' : '#eee', color: themeHelper.text }} />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {(!dados?.recentes_executores || dados.recentes_executores.length === 0) && (
                                <TableRow><TableCell colSpan={2} sx={{ py: 2, textAlign: 'center', color: themeHelper.subText }}>Nenhum executor ativo.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );

    const RenderUltimasOS = () => (
        <Card sx={{ bgcolor: themeHelper.paper, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" mb={1}>
                    <TimelineIcon sx={{ color: themeHelper.primary, mr: 1, fontSize: 24 }} />
                    <Typography variant="subtitle1" fontWeight="bold" color={themeHelper.text}>Últimas Ordens Adicionadas</Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 430, overflowY: 'auto', border: `1px solid ${themeHelper.border}`, borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>OS</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Solicitação</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Instalação</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Criador</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Status / Etapa</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Detalhes Agendamento</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Cronograma</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {renderOSTableRows(dados?.ultimas_os)}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );

    const RenderOSParadas = () => (
        <Card sx={{ bgcolor: themeHelper.paper, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                    <ReportProblemIcon sx={{ color: '#ff9800', mr: 1, fontSize: 32 }} />
                    <Typography variant="h6" color={themeHelper.text}>Ordens Abertas Mais Antigas</Typography>
                </Box>
                <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent', border: `1px solid ${themeHelper.border}`, maxHeight: 430, overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>OS</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Solicitação</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Instalação</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Criador</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Status / Etapa</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Detalhes Agendamento</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold', py: 0.5 }}>Cronograma</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {renderOSTableRows(dados?.os_paradas)}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );

    const RenderProcedimentosParados = () => (
        <Card sx={{ bgcolor: themeHelper.paper, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
            <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                    <AssignmentLateIcon sx={{ color: '#f44336', mr: 1, fontSize: 32 }} />
                    <Typography variant="h6" color={themeHelper.text}>Top Procedimentos Pendentes</Typography>
                </Box>
                <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent', border: `1px solid ${themeHelper.border}`, maxHeight: 400, overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold' }}>ID</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold' }}>Procedimento</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold' }}>Executor</TableCell>
                                <TableCell sx={{ bgcolor: themeHelper.tableHead, color: themeHelper.text, fontWeight: 'bold' }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(dados?.procedimentos_parados || []).map((proc, i) => (
                                <TableRow key={i} hover>
                                    <TableCell sx={{ color: themeHelper.text }}>{proc.id_procedimento_execucao}</TableCell>
                                    <TableCell sx={{ color: themeHelper.text, maxWidth: 150 }} noWrap>{proc.procedimento_nome || '-'}</TableCell>
                                    <TableCell sx={{ color: themeHelper.text }}>{proc.executor || 'Não Definido'}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={formataStatusProced(proc.status).label} color={formataStatusProced(proc.status).color} />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {(!dados?.procedimentos_parados || dados.procedimentos_parados.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ color: themeHelper.subText, py: 3 }}>
                                        <CheckCircleIcon sx={{ color: themeHelper.secondary, mb: 1, fontSize: 30 }} /><br />
                                        Todos os procedimentos em dia.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" bgcolor={themeHelper.bg}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    return (
        <MuiThemeProvider theme={muiTheme}>
            <CoreThemeProvider theme={coreTheme}>
                <Box sx={{
                    p: isMobile ? 1.5 : 3,
                    bgcolor: themeHelper.bg,
                    minHeight: '100vh',
                    maxHeight: '100vh',
                    overflowY: 'auto',
                    fontFamily: '"Inter", sans-serif',
                    color: themeHelper.text
                }}>
                    <Box mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                        <Box>
                            <Typography variant="h5" fontWeight="700" color={themeHelper.text}>
                                Visão Estratégica
                            </Typography>
                            <Typography variant="body2" color={themeHelper.subText}>
                                Acompanhamento gerencial das movimentações e gargalos.
                            </Typography>
                        </Box>
                    </Box>

                    <RenderKPIs />

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <RenderUltimasOS />
                        </Grid>
                        {/* 
                        <Grid item xs={12} lg={4}>
                            <RenderExecutoresRecentes />
                        </Grid>
                        */}

                        <Grid item xs={12} lg={8}>
                            <RenderOSParadas />
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <RenderRankingSolicitacoes />
                        </Grid>

                        <Grid item xs={12}>
                            <RenderProcedimentosParados />
                        </Grid>
                    </Grid>
                </Box>
            </CoreThemeProvider>
        </MuiThemeProvider>
    );
}
